import { FC, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Button, Tooltip } from '@material-ui/core';
import { SaveModal } from '@components/Modals';
import { setIsOpenSaveAsModal } from '@actions/lettersPageActions';
import { SaveAsIcon } from '@images/icons';
import { CreateSubmissionRequestInterface, MgState, CreateSampleInterface } from '@types';
import { getSubmissionGroupListSelector } from '@selectors/submissionGroups';
import { submissionsTypes } from '@constants/submissionsTypes';
import { makeCreateSubmission } from '@actions/submissionActions';
import { SubmissionTypes } from '@enums';
import {
  canSaveAsTransferToCommonSubmissions,
  canSaveAsTransferToOwnSubmissions,
  canTransferSampleSubmissionSelector,
  currentSubmissionGroupId,
  currentSubmissionSelector,
  currentSubmissionTemplateEditorFormChanged,
  isCurrentSubmissionTemplateEditorModeOn,
  isSubmissionSaveAsDisabledSelector,
  submissionsSelector,
} from '@selectors/submission';
import { useSetGroupTemplateId } from '@hooks/useSetGroupTemplateId';
import { translate } from "@utils";

export interface SaveAsControllerProps {
  description: string;
}

const SaveAsController: FC<SaveAsControllerProps> = ({ description }) => {
  const [submissionIsCommon, setSubmissionIsCommon] = useState(false);
  const [submissionName, setSubmissionName] = useState('');
  const [modalTemplateType, setModalTemplateType] = useState('');
  const [groupTemplateId, setGroupTemplateId] = useState<string>('');

  const currentSubmission = useSelector(currentSubmissionSelector);
  const currentGroupId = useSelector(currentSubmissionGroupId);
  const submissions = useSelector(submissionsSelector);
  const isSubmissionCreateLoading = useSelector<MgState, boolean>(
    ({ submission }) => submission.isSubmissionCreateLoading
  );
  const isOpenSaveAsModal = useSelector<MgState, boolean>(
    ({ lettersPage }) => lettersPage.isOpenSaveAsModal
  );
  const isCurrentSubmissionTemplateEditorFormChanged = useSelector(
    currentSubmissionTemplateEditorFormChanged
  );
  const isIconDisabled = useSelector(isSubmissionSaveAsDisabledSelector);
  const submissionGroups = useSelector(getSubmissionGroupListSelector);
  const isTemplateEditorMode = useSelector(isCurrentSubmissionTemplateEditorModeOn);
  const dispatch = useDispatch();
  const hasPermissionTransferToCommonSubmissions = useSelector(
    canSaveAsTransferToCommonSubmissions
  );
  const hasPermissionTransferToOwnSubmissions = useSelector(canSaveAsTransferToOwnSubmissions);
  const isGroupTemplateIdSettable = useSetGroupTemplateId(groupTemplateId, submissionIsCommon);
  const hasPermissionTransferSampleSubmissions = useSelector(canTransferSampleSubmissionSelector);

  useEffect(() => {
    if (isGroupTemplateIdSettable) {
      setGroupTemplateId(currentGroupId);
    }
    if (isTemplateEditorMode) {
      setModalTemplateType(SubmissionTypes.Common);
    }
    return () => {
      if (isTemplateEditorMode) {
        setModalTemplateType('');
      }
    };
  }, [isTemplateEditorMode, isOpenSaveAsModal, submissionIsCommon]);

  const isGroupSelectDisabled = () => {
    if (isTemplateEditorMode) {
      if (modalTemplateType === SubmissionTypes.Common) {
        return !hasPermissionTransferSampleSubmissions;
      }
    }
    if (modalTemplateType === SubmissionTypes.Own) {
      return !hasPermissionTransferToOwnSubmissions;
    }
    if (modalTemplateType === SubmissionTypes.Common) {
      return !hasPermissionTransferToCommonSubmissions;
    }
    return true;
  };

  const handleOnNameChange = useCallback((event) => {
    setSubmissionName(event.currentTarget.value);
  }, []);

  const submissionSaveAsTypes = submissionsTypes.filter(
    (option) => option.value !== SubmissionTypes.All && option.value !== SubmissionTypes.Private
  );

  const handleOnTemplateTypeChange = useCallback(
    (value: SubmissionTypes) => {
      setModalTemplateType(value);
      if (value === SubmissionTypes.Common) {
        setSubmissionIsCommon(true);
      } else {
        setSubmissionIsCommon(false);
      }
    },
    [modalTemplateType, submissionsTypes, submissionIsCommon]
  );

  const handleOnGroupTemplateIdChange = useCallback((value: string) => {
    setGroupTemplateId(value);
  }, []);

  const handleSaveAs = useCallback(() => {
    if (submissionName) {
      const result = !isTemplateEditorMode
        ? ({
            title: submissionName,
            groupId: groupTemplateId,
            isCommon: submissionIsCommon,
            data: currentSubmission.data,
            formName: currentSubmission.origin?.form?.name,
            originId: currentSubmission.origin?.id,
          } as CreateSubmissionRequestInterface)
        : ({
            title: submissionName,
            baseOriginId: currentSubmission.origin?.id,
            isCommon: true,
            groupId: groupTemplateId,
            incomingOriginSections: currentSubmission.origin.originSections,
          } as CreateSampleInterface);

      dispatch(makeCreateSubmission(result, isTemplateEditorMode));
      setSubmissionName('');
      setModalTemplateType('');
      setGroupTemplateId(null);
    }
  }, [submissionName, currentSubmission, submissionIsCommon, modalTemplateType, groupTemplateId]);

  const handleCancelSaveAs = useCallback(() => {
    dispatch(setIsOpenSaveAsModal(false));
    setSubmissionName('');
    setModalTemplateType('');
    setGroupTemplateId(null);
  }, []);

  return (
    <>
      <Tooltip title={isTemplateEditorMode ? translate('save_as_new_sample') : description} arrow>
        <span>
          <Button
            size="large"
            color="primary"
            onClick={() => dispatch(setIsOpenSaveAsModal(true))}
            disabled={isEmpty(submissions) || isIconDisabled}
            disableRipple
          >
            <SaveAsIcon />
          </Button>
        </span>
      </Tooltip>

      <SaveModal
        open={isOpenSaveAsModal}
        value={submissionName}
        submissionGroups={submissionGroups}
        modalTemplateType={modalTemplateType}
        submissionsTypes={submissionSaveAsTypes}
        groupTemplateId={groupTemplateId}
        contentName="template_name"
        isGroupSelectDisable={isGroupSelectDisabled()}
        handleCancel={handleCancelSaveAs}
        handleConfirmation={handleSaveAs}
        handleOnTemplateTypeChange={handleOnTemplateTypeChange}
        handleOnGroupTemplateIdChange={handleOnGroupTemplateIdChange}
        onChange={handleOnNameChange}
        isDisabledConfirmationButton={isSubmissionCreateLoading}
        isCurrentSubmissionTemplateEditorFormChanged={isCurrentSubmissionTemplateEditorFormChanged}
        isSampleSaving={isTemplateEditorMode}
      />
    </>
  );
};

export { SaveAsController };
